import {
  ArrowDownWideNarrow,
  CheckSquare,
  Eraser,
  Lock,
  MessageSquare,
  MessagesSquare,
  Pencil,
  RefreshCw,
  RotateCcw,
  Search,
  Square,
  Trash2,
  Unlock,
  Users,
} from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Modal } from "@/components/ui/modal";
import { cn } from "@/lib/utils/cn";
import i18n from "@/lib/i18n/i18n";

import { actionPlanKeys } from "../../api/keys";
import {
  dismissPrioritizationTour,
  patchAction,
  useClearConsultationResponses,
  usePrioritization,
  useReorderActions,
  useSetPrePrioritization,
  useToggleConsultation,
  useSetActionSelection,
  useUnselectActions,
} from "../../api/hooks";
import type {
  ActionPlanAction,
  ActionPlanDetail,
  PrioritizationActionScore,
} from "../../api/types";
import { actionName, goalName, patchPlanActions } from "../../lib/selection";
import { useStepTour, type TourStep } from "../../lib/tour";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { StepIntro } from "./StepIntro";

/** Format a score with the active locale's decimal separator (FR → comma). */
function fmtScore(n: number): string {
  return n.toLocaleString(i18n.language?.startsWith("fr") ? "fr-CA" : "en-CA", {
    maximumFractionDigits: 1,
  });
}

interface Row {
  action: ActionPlanAction;
  thematicName: string;
  issueName: string;
  goalLabel: string;
}

/**
 * Flatten the plan's selected actions, carrying thematic/issue/goal context.
 * `keepIds` keeps rows the user removed *this session* visible (unchecked) so
 * they can be re-added in place — matching the old app's toggle behavior.
 */
function selectedRows(plan: ActionPlanDetail, keepIds: Set<string>): Row[] {
  const rows: Row[] = [];
  for (const thematic of plan.action_plan_thematics) {
    for (const issue of thematic.action_plan_issues) {
      for (const goal of issue.action_plan_goals) {
        for (const action of goal.action_plan_actions) {
          if (action.is_selected || keepIds.has(action.id)) {
            rows.push({
              action,
              thematicName: thematic.name,
              issueName: issue.name,
              goalLabel: goalName(goal),
            });
          }
        }
      }
    }
  }
  return rows.sort(
    (a, b) => (a.action.priority_order ?? 0) - (b.action.priority_order ?? 0),
  );
}

const PRIORITIES = [1, 2, 3, 4, 5] as const;

const SELECT_CLS =
  "w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function FilterField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function PrioritizationStep({ plan }: { plan: ActionPlanDetail }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const priorQuery = usePrioritization(plan.id);
  const reorder = useReorderActions(plan.id);
  const unselect = useUnselectActions(plan.id);
  // Selection writes go through the optimistic bulk hook so the tick flips in
  // the same frame as the click.
  const setSelection = useSetActionSelection(plan.id);
  const setPrePrio = useSetPrePrioritization(plan.id);
  const toggle = useToggleConsultation(plan.id);
  const clear = useClearConsultationResponses(plan.id);
  // Actions removed this session stay visible (unchecked) so they can be
  // re-added in place — the old app's toggle behavior.
  const [sessionKept, setSessionKept] = useState<Set<string>>(() => new Set());
  const [confirmRemoveRow, setConfirmRemoveRow] = useState<Row | null>(null);
  const [rewordRow, setRewordRow] = useState<Row | null>(null);
  const [commentsRow, setCommentsRow] = useState<Row | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: actionPlanKeys.detail(plan.id) });

  const refreshScores = async () => {
    setRefreshing(true);
    try {
      await priorQuery.refetch();
      toast.success(t("actionPlans.prioritization.refreshed"));
    } finally {
      setRefreshing(false);
    }
  };

  const doRemove = (row: Row) => {
    // Keep the row on screen (unticked) so it can be re-added in place.
    setSessionKept((prev) => new Set(prev).add(row.action.id));
    setSelection.mutate({ actionIds: [row.action.id], isSelected: false });
    setConfirmRemoveRow(null);
  };

  const reAdd = (row: Row) => {
    // The id stays in sessionKept: a selected action shows anyway, and dropping
    // it here used to hide the row until the refetch landed — for that moment
    // the action was neither selected nor kept, so it vanished and came back.
    setSelection.mutate({ actionIds: [row.action.id], isSelected: true });
  };

  const saveReword = async (text: string) => {
    if (!rewordRow) return;
    const actionId = rewordRow.action.id;
    await patchAction(actionId, { custom_name: text });
    // Show the new wording as the modal closes rather than a beat later. The
    // server derives use_custom_action_name from whether any text remains, so
    // mirror that here or the row would keep rendering the catalogue name.
    qc.setQueryData<ActionPlanDetail>(actionPlanKeys.detail(plan.id), (cached) =>
      cached
        ? patchPlanActions(cached, [actionId], {
            custom_name: text,
            use_custom_action_name: Boolean(text),
          })
        : cached,
    );
    void invalidate();
  };

  const buildSteps = (): TourStep[] => [
    { selector: '[data-tour="ap-progress"]', title: t("actionPlans.tour.prioritization.progressTitle"), description: t("actionPlans.tour.prioritization.progressDesc") },
    { selector: '[data-tour="pr-consultation"]', title: t("actionPlans.tour.prioritization.consultationTitle"), description: t("actionPlans.tour.prioritization.consultationDesc") },
    { selector: '[data-tour="pr-filters"]', title: t("actionPlans.tour.prioritization.filtersTitle"), description: t("actionPlans.tour.prioritization.filtersDesc") },
    { selector: '[data-tour="pr-autosort"]', title: t("actionPlans.tour.prioritization.autoSortTitle"), description: t("actionPlans.tour.prioritization.autoSortDesc") },
    { selector: '[data-tour="pr-table"]', title: t("actionPlans.tour.prioritization.tableTitle"), description: t("actionPlans.tour.prioritization.tableDesc"), side: "top" },
    { selector: '[data-tour="pr-prio"]', title: t("actionPlans.tour.prioritization.prePrioTitle"), description: t("actionPlans.tour.prioritization.prePrioDesc") },
    { selector: '[data-tour="pr-comment"]', title: t("actionPlans.tour.prioritization.commentsTitle"), description: t("actionPlans.tour.prioritization.commentsDesc") },
  ];
  const { replay } = useStepTour({
    buildSteps,
    autoRun: plan.show_prioritization_tour ?? false,
    autoRunKey: `prioritization:${plan.id}`,
    // Same as step 2: the dismissal only reaches the client on the next fetch.
    onAutoDismiss: () => {
      void dismissPrioritizationTour(plan.id).finally(() =>
        qc.invalidateQueries({ queryKey: actionPlanKeys.detail(plan.id) }),
      );
    },
  });

  const [query, setQuery] = useState("");
  const [thematic, setThematic] = useState("");
  const [issue, setIssue] = useState("");
  const [goal, setGoal] = useState("");
  const [scoreMin, setScoreMin] = useState("");
  const [scoreMax, setScoreMax] = useState("");
  const [consulted, setConsulted] = useState<"all" | "yes" | "no">("all");
  const [groupFilter, setGroupFilter] = useState("");

  const rows = useMemo(() => selectedRows(plan, sessionKept), [plan, sessionKept]);
  const scoreById = useMemo(() => {
    const map = new Map<string, PrioritizationActionScore>();
    for (const s of priorQuery.data?.actions ?? []) map.set(s.action_id, s);
    return map;
  }, [priorQuery.data]);
  const groups = priorQuery.data?.groups ?? [];

  // Filter option lists, scoped so Enjeu depends on the chosen Thématique and
  // Objectif on the chosen Enjeu (mirrors the old cascading selects).
  const thematicOptions = useMemo(
    () => [...new Set(rows.map((r) => r.thematicName))].sort(),
    [rows],
  );
  const issueOptions = useMemo(
    () =>
      [
        ...new Set(
          rows
            .filter((r) => !thematic || r.thematicName === thematic)
            .map((r) => r.issueName),
        ),
      ].sort(),
    [rows, thematic],
  );
  const goalOptions = useMemo(
    () =>
      [
        ...new Set(
          rows
            .filter((r) => !thematic || r.thematicName === thematic)
            .filter((r) => !issue || r.issueName === issue)
            .map((r) => r.goalLabel),
        ),
      ].sort(),
    [rows, thematic, issue],
  );

  const isConsulted = (sd: PrioritizationActionScore | undefined) =>
    (sd?.score ?? 0) > 0 ||
    (sd?.comment_count ?? 0) > 0 ||
    (sd?.group_scores.some((g) => g.score > 0) ?? false);

  const parseScore = (v: string) => {
    const n = parseFloat(v.replace(",", "."));
    return Number.isNaN(n) ? null : n;
  };

  const q = query.trim().toLowerCase();
  const min = parseScore(scoreMin);
  const max = parseScore(scoreMax);
  const filtersActive = Boolean(
    q || thematic || issue || goal || min !== null || max !== null || consulted !== "all" || groupFilter,
  );

  const visible = rows.filter((r) => {
    if (
      q &&
      // Search the action name AND its thematic/issue/goal breadcrumb (parity
      // with the old app's haystack), not just the name.
      ![actionName(r.action), r.thematicName, r.issueName, r.goalLabel]
        .join(" ")
        .toLowerCase()
        .includes(q)
    )
      return false;
    if (thematic && r.thematicName !== thematic) return false;
    if (issue && r.issueName !== issue) return false;
    if (goal && r.goalLabel !== goal) return false;
    const sd = scoreById.get(r.action.id);
    const score = sd?.score ?? 0;
    if (min !== null && score < min) return false;
    if (max !== null && score > max) return false;
    if (consulted === "yes" && !isConsulted(sd)) return false;
    if (consulted === "no" && isConsulted(sd)) return false;
    if (groupFilter) {
      const gs = sd?.group_scores.find((x) => x.group_id === groupFilter);
      if (!gs || gs.score <= 0) return false;
    }
    return true;
  });

  const resetFilters = () => {
    setQuery("");
    setThematic("");
    setIssue("");
    setGoal("");
    setScoreMin("");
    setScoreMax("");
    setConsulted("all");
    setGroupFilter("");
  };

  const autoSort = () => {
    const sorted = [...rows].sort(
      (a, b) =>
        (scoreById.get(b.action.id)?.score ?? 0) -
        (scoreById.get(a.action.id)?.score ?? 0),
    );
    reorder.mutate(sorted.map((r) => r.action.id));
  };

  // Actions hidden by the current filter but still in the plan — candidates for
  // the bulk "remove out-of-filter" review modal.
  const hiddenRows = rows.filter(
    (r) => r.action.is_selected && !visible.includes(r),
  );

  type ScoreData = ReturnType<typeof scoreById.get>;

  /** Score shown in the Score cell: the filtered group's score when a
   *  stakeholder category filter is active, else the aggregate. */
  const displayScore = (sd: ScoreData): number => {
    if (groupFilter) {
      return sd?.group_scores.find((g) => g.group_id === groupFilter)?.score ?? 0;
    }
    return sd?.score ?? 0;
  };

  // Shared between the desktop matrix and the mobile cards.
  const renderActionCell = (row: Row, scoreData: ScoreData) => (
    <>
      <div className="flex items-start gap-1.5">
        <span
          className={cn(
            "font-medium",
            !row.action.is_selected && "text-muted-foreground line-through",
          )}
        >
          {actionName(row.action)}
        </span>
        {row.action.comment && (
          <MessageSquare
            className="mt-0.5 size-3.5 shrink-0 text-primary"
            aria-label={t("actionPlans.prioritization.ownComment")}
          />
        )}
        <span className="ml-auto flex shrink-0 items-center gap-0.5">
          {/* Stakeholder comments sit with the row's other controls rather than
              trailing the wording. Conditional, so it goes inside the two every
              row carries — same order rule as the other steps. */}
          {scoreData && scoreData.comment_count > 0 && (
            <IconBtn
              title={t("actionPlans.prioritization.stakeholderComments", {
                count: scoreData.comment_count,
              })}
              className="flex w-auto gap-0.5 px-1.5 text-theme-economic-ink"
              onClick={() => setCommentsRow(row)}
            >
              <MessagesSquare />
              <span className="text-[11px] font-bold">{scoreData.comment_count}</span>
            </IconBtn>
          )}
          <IconBtn
            title={t("actionPlans.actions.rewordAction")}
            onClick={() => setRewordRow(row)}
          >
            <Pencil />
          </IconBtn>
          {/* Toggle in/out of the plan — checked = in the plan. Removing asks
              for confirmation; a removed row stays visible so it can be
              re-added in place (old app's btn-toggle-priority). */}
          {row.action.is_selected ? (
            <IconBtn
              title={t("actionPlans.prioritization.removeFromPlan")}
              className="text-primary"
              onClick={() => setConfirmRemoveRow(row)}
            >
              <CheckSquare />
            </IconBtn>
          ) : (
            <IconBtn
              title={t("actionPlans.prioritization.addToPlan")}
              onClick={() => reAdd(row)}
            >
              <Square />
            </IconBtn>
          )}
        </span>
      </div>
      <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
        {row.thematicName} › {row.issueName} › {row.goalLabel}
      </p>
    </>
  );

  const renderPrioSelect = (row: Row, scoreData: ScoreData) => {
    // Every action can be rated. A questionnaire action's rating is the
    // self-assessment « Importance », shared by all the member's plans; a
    // custom action's is kept on this plan.
    return (
      <select
        aria-label={t("actionPlans.prioritization.workingGroupPriority")}
        value={scoreData?.pre_prio ?? ""}
        onChange={(e) =>
          setPrePrio.mutate({
            actionId: row.action.id,
            priority: e.target.value ? Number(e.target.value) : null,
          })
        }
        className="rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="">—</option>
        {PRIORITIES.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <StepIntro
          heading={t("actionPlans.prioritization.heading")}
          help={t("actionPlans.prioritization.help")}
        />
        <button
          type="button"
          onClick={replay}
          className="shrink-0 text-sm font-semibold text-primary hover:underline"
        >
          {t("actionPlans.tour.replay")}
        </button>
      </div>

      {rows.length === 0 ? (
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-muted-foreground">
              {t("actionPlans.prioritization.noSelected")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Consultation results */}
          <div className="overflow-hidden rounded-xl border" data-tour="pr-consultation">
            <div className="flex items-center gap-2 border-b bg-muted/40 px-4 py-3">
              <Users className="size-4 text-muted-foreground" aria-hidden />
              <h3 className="text-sm font-bold">
                {t("actionPlans.prioritization.consultationResults")}
              </h3>
              <span className="ml-auto text-xs text-muted-foreground">
                {t("actionPlans.prioritization.categoriesCount", {
                  count: groups.length,
                })}
              </span>
            </div>
            {groups.length === 0 ? (
              <p className="px-4 py-4 text-sm text-muted-foreground">
                {t("actionPlans.prioritization.noGroups")}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2">{t("actionPlans.prioritization.group")}</th>
                    <th className="px-4 py-2 text-center">{t("actionPlans.prioritization.status")}</th>
                    <th className="px-4 py-2 text-center">{t("actionPlans.prioritization.responses")}</th>
                    <th className="px-4 py-2 text-center">{t("actionPlans.prioritization.manage")}</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.id} className="border-b last:border-0">
                      <td className="px-4 py-2.5 font-medium">{g.name}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span
                          className={cn(
                            "rounded-full px-2.5 py-0.5 text-xs font-bold",
                            g.is_open
                              ? "bg-success/10 text-success"
                              : "bg-secondary text-muted-foreground",
                          )}
                        >
                          {g.is_open
                            ? t("actionPlans.prioritization.open")
                            : t("actionPlans.prioritization.closed")}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center tabular-nums">
                        {g.response_count}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-center gap-1.5">
                          <IconBtn
                            title={
                              g.is_open
                                ? t("actionPlans.prioritization.closeConsultation")
                                : t("actionPlans.prioritization.openConsultation")
                            }
                            onClick={() =>
                              toggle.mutate({ id: g.consultation_id, is_open: !g.is_open })
                            }
                          >
                            {g.is_open ? <Unlock /> : <Lock />}
                          </IconBtn>
                          <IconBtn
                            title={t("actionPlans.prioritization.clearResponses")}
                            className="text-theme-social-ink"
                            onClick={() => {
                              if (window.confirm(t("actionPlans.prioritization.confirmClear")))
                                clear.mutate(g.consultation_id);
                            }}
                          >
                            <Eraser />
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Individual responses are anonymized — trust/privacy notice. */}
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="size-3.5 shrink-0" aria-hidden />
            {t("actionPlans.prioritization.anonymised")}
          </p>

          {/* Toolbar */}
          <div className="space-y-3" data-tour="pr-filters">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-lg border px-3 py-2">
                <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("actionPlans.prioritization.search")}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-placeholder"
                />
              </div>
              <Button variant="outline" size="sm" onClick={autoSort} disabled={reorder.isPending} data-tour="pr-autosort">
                <ArrowDownWideNarrow aria-hidden />
                {t("actionPlans.prioritization.autoSort")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refreshScores()}
                disabled={refreshing}
              >
                <RefreshCw
                  aria-hidden
                  className={refreshing ? "animate-spin" : undefined}
                />
                {t("actionPlans.prioritization.refreshScores")}
              </Button>
              {hiddenRows.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={() => setBulkOpen(true)}
                  disabled={unselect.isPending}
                >
                  <Trash2 aria-hidden />
                  {t("actionPlans.prioritization.bulkRemoveButton", {
                    count: hiddenRows.length,
                  })}
                </Button>
              )}
              {filtersActive && (
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  <RotateCcw aria-hidden />
                  {t("actionPlans.prioritization.resetFilters")}
                </Button>
              )}
            </div>

            {/* Filters — thematic → issue → goal cascade, score range, consulted,
                stakeholder category (matches the old prioritization filter bar). */}
            <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-2 lg:grid-cols-4">
              <FilterField label={t("actionPlans.prioritization.filterThematic")}>
                <select
                  value={thematic}
                  onChange={(e) => {
                    setThematic(e.target.value);
                    setIssue("");
                    setGoal("");
                  }}
                  className={SELECT_CLS}
                >
                  <option value="">{t("actionPlans.prioritization.filterAllF")}</option>
                  {thematicOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label={t("actionPlans.prioritization.filterIssue")}>
                <select
                  value={issue}
                  onChange={(e) => {
                    setIssue(e.target.value);
                    setGoal("");
                  }}
                  className={SELECT_CLS}
                >
                  <option value="">{t("actionPlans.prioritization.filterAllM")}</option>
                  {issueOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label={t("actionPlans.prioritization.filterGoal")}>
                <select value={goal} onChange={(e) => setGoal(e.target.value)} className={SELECT_CLS}>
                  <option value="">{t("actionPlans.prioritization.filterAllM")}</option>
                  {goalOptions.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label={t("actionPlans.prioritization.consulted")}>
                <select
                  value={consulted}
                  onChange={(e) => setConsulted(e.target.value as "all" | "yes" | "no")}
                  className={SELECT_CLS}
                >
                  <option value="all">{t("actionPlans.prioritization.filterAllM")}</option>
                  <option value="yes">{t("actionPlans.prioritization.consultedYes")}</option>
                  <option value="no">{t("actionPlans.prioritization.consultedNo")}</option>
                </select>
              </FilterField>

              <FilterField label={t("actionPlans.prioritization.scoreMin")}>
                <input
                  inputMode="decimal"
                  value={scoreMin}
                  onChange={(e) => setScoreMin(e.target.value)}
                  className={SELECT_CLS}
                />
              </FilterField>

              <FilterField label={t("actionPlans.prioritization.scoreMax")}>
                <input
                  inputMode="decimal"
                  value={scoreMax}
                  onChange={(e) => setScoreMax(e.target.value)}
                  className={SELECT_CLS}
                />
              </FilterField>

              {groups.length > 0 && (
                <FilterField
                  label={t("actionPlans.prioritization.stakeholderCategory")}
                  className="sm:col-span-2"
                >
                  <select
                    value={groupFilter}
                    onChange={(e) => setGroupFilter(e.target.value)}
                    className={SELECT_CLS}
                  >
                    <option value="">{t("actionPlans.prioritization.filterAllF")}</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </FilterField>
              )}
            </div>
          </div>

          {/* Mobile: one ranked card per action. The desktop matrix (# · action ·
              score · priority · a column per stakeholder group) is far too wide. */}
          <ul className="space-y-2 md:hidden">
            {visible.map((row, index) => {
              const scoreData = scoreById.get(row.action.id);
              return (
                <li key={row.action.id} className="rounded-lg border p-3">
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 shrink-0 text-sm font-bold tabular-nums text-muted-foreground">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      {renderActionCell(row, scoreData)}
                    </div>
                  </div>
                  <dl className="mt-3 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1.5 text-sm">
                    <dt className="text-muted-foreground">
                      {t("actionPlans.prioritization.score")}
                    </dt>
                    <dd>
                      {displayScore(scoreData) > 0 ? (
                        <span className="font-bold tabular-nums text-theme-governance-ink">
                          {fmtScore(displayScore(scoreData))}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </dd>
                    <dt className="text-muted-foreground">
                      {t("actionPlans.prioritization.workingGroupPriority")}
                    </dt>
                    <dd>{renderPrioSelect(row, scoreData)}</dd>
                    {groups.map((g) => {
                      const gs = scoreData?.group_scores.find(
                        (x) => x.group_id === g.id,
                      );
                      return (
                        <Fragment key={g.id}>
                          <dt className="text-muted-foreground">{g.name}</dt>
                          <dd className="tabular-nums text-muted-foreground">
                            {gs && gs.score > 0 ? gs.score : "—"}
                          </dd>
                        </Fragment>
                      );
                    })}
                  </dl>
                </li>
              );
            })}
          </ul>

          {/* Prioritization table */}
          <div className="hidden overflow-x-auto rounded-xl border md:block" data-tour="pr-table">
            <table className="w-full min-w-[760px] border-collapse text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left align-bottom text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  <th className="w-10 px-3 py-2 text-center">#</th>
                  <th className="min-w-[280px] px-3 py-2">
                    {t("actionPlans.prioritization.actionColumn")}
                  </th>
                  <th className="w-16 px-3 py-2 text-center">
                    {t("actionPlans.prioritization.score")}
                  </th>
                  <th className="w-24 px-3 py-2 text-center">
                    {t("actionPlans.prioritization.workingGroupPriority")}
                  </th>
                  {groups.map((g) => (
                    <th key={g.id} className="w-20 px-3 py-2 text-center">
                      {g.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row, index) => {
                  const scoreData = scoreById.get(row.action.id);
                  return (
                    <tr key={row.action.id} className="border-b align-top last:border-0">
                      <td className="px-3 py-2.5 text-center tabular-nums text-muted-foreground">
                        {index + 1}
                      </td>
                      <td
                        className="px-3 py-2.5"
                        data-tour={index === 0 ? "pr-comment" : undefined}
                      >
                        {renderActionCell(row, scoreData)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {displayScore(scoreData) > 0 ? (
                          <span className="font-bold tabular-nums text-theme-governance-ink">
                            {fmtScore(displayScore(scoreData))}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td
                        className="px-3 py-2.5 text-center"
                        data-tour={index === 0 ? "pr-prio" : undefined}
                      >
                        {renderPrioSelect(row, scoreData)}
                      </td>
                      {groups.map((g) => {
                        const gs = scoreData?.group_scores.find(
                          (x) => x.group_id === g.id,
                        );
                        return (
                          <td
                            key={g.id}
                            className="px-3 py-2.5 text-center tabular-nums text-muted-foreground"
                          >
                            {gs && gs.score > 0 ? fmtScore(gs.score) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {confirmRemoveRow && (
        <ConfirmDialog
          cancelLabel={t("actionPlans.actions.cancel")}
          title={t("actionPlans.prioritization.confirmRemoveActionTitle")}
          message={t("actionPlans.prioritization.confirmRemoveAction")}
          confirmLabel={t("actionPlans.prioritization.removeFromPlan")}
          busy={unselect.isPending}
          onConfirm={() => doRemove(confirmRemoveRow)}
          onClose={() => setConfirmRemoveRow(null)}
        />
      )}

      {rewordRow && (
        <RewordActionModal
          initial={actionName(rewordRow.action)}
          original={rewordRow.action.name}
          onSave={saveReword}
          onClose={() => setRewordRow(null)}
        />
      )}

      {commentsRow && (
        <Modal
          title={t("actionPlans.prioritization.stakeholderComments")}
          onClose={() => setCommentsRow(null)}
        >
          <div className="space-y-4">
            <p className="text-sm font-medium">{actionName(commentsRow.action)}</p>
            <ul className="space-y-2.5">
              {(scoreById.get(commentsRow.action.id)?.comments ?? []).map(
                (c, index) => (
                  <li
                    key={index}
                    className="rounded-md border border-border bg-muted/40 p-3"
                  >
                    <p className="text-xs font-bold uppercase tracking-wide text-theme-economic-ink">
                      {c.stakeholder_group__name}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm">
                      {c.comment}
                    </p>
                  </li>
                ),
              )}
            </ul>
          </div>
        </Modal>
      )}

      {bulkOpen && (
        <BulkRemoveModal
          rows={hiddenRows}
          busy={unselect.isPending}
          onConfirm={(ids) => {
            unselect.mutate(ids);
            setBulkOpen(false);
          }}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </div>
  );
}

/** Reword an action's wording on Step 3 (reuses the Step-2 derivation: the
 *  server sets use_custom_action_name from the text). */
function RewordActionModal({
  initial,
  original,
  onSave,
  onClose,
}: {
  initial: string;
  original: string;
  onSave: (text: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await onSave(value.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={t("actionPlans.actions.rewordAction")} onClose={onClose}>
      <div className="space-y-4">
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">
            {t("actionPlans.actions.rewordActionLabel")}
          </span>
          <textarea
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={original}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("actionPlans.actions.cancel")}
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {t("actionPlans.actions.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Review modal for the bulk "remove out-of-filter" action: lists the hidden
 *  actions, each uncheckable to spare it, with a live count. */
function BulkRemoveModal({
  rows,
  busy,
  onConfirm,
  onClose,
}: {
  rows: Row[];
  busy: boolean;
  onConfirm: (ids: string[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Default: every hidden action is marked for removal; unchecking spares it.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(rows.map((r) => r.action.id)),
  );
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <Modal
      title={t("actionPlans.prioritization.bulkRemoveTitle")}
      onClose={onClose}
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("actionPlans.prioritization.bulkRemoveIntro")}
        </p>
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {rows.map((r) => (
            <li key={r.action.id}>
              <label className="flex cursor-pointer items-start gap-2 rounded-md p-2 hover:bg-accent">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-primary"
                  checked={selected.has(r.action.id)}
                  onChange={() => toggle(r.action.id)}
                />
                <span>
                  <span className="text-sm font-medium">{actionName(r.action)}</span>
                  <span className="block text-[11px] text-muted-foreground">
                    {r.thematicName} › {r.issueName} › {r.goalLabel}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t("actionPlans.actions.cancel")}
          </Button>
          <Button
            variant="destructive"
            disabled={busy || selected.size === 0}
            onClick={() => onConfirm([...selected])}
          >
            {t("actionPlans.prioritization.bulkRemoveConfirm", {
              count: selected.size,
            })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function IconBtn({
  title,
  onClick,
  className,
  children,
}: {
  title: string;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={cn(
        "grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground [&_svg]:size-4",
        className,
      )}
    >
      {children}
    </button>
  );
}
